import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { SOCIAL } from '../utils/constants';
import Reveal from '../components/common/Reveal';
import Logo from '../components/common/Logo';
import { SectionHeader, BtnPrimary, BtnOutline, Card, Tag, Counter } from '../components/ui';

export default function HomePage() {
  const [videos, setVideos] = useState([]);
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [stats, setStats] = useState({});
  const [playing, setPlaying] = useState(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    api.get('/videos').then(r => setVideos(r.data)).catch(() => {});
    api.get('/articles/featured').then(r => setArticles(r.data)).catch(() => {});
    api.get('/news').then(r => setNews(r.data.slice(0, 4))).catch(() => {});
    api.get('/gallery').then(r => setGallery(r.data.slice(0, 4))).catch(() => {});
    api.get('/stats/public').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const text = 'Where the Soul Finds Its Voice';
    let i = 0;
    const iv = setInterval(() => { i++; setTyped(text.slice(0, i)); if (i >= text.length) clearInterval(iv); }, 60);
    return () => clearInterval(iv);
  }, []);

  const marqueeText = '  ✦  Sufism  ✦  Literature  ✦  Art  ✦  Culture  ✦  Poetry  ✦  Mysticism  ✦  Heritage  ✦  Wisdom  ';

  return (
    <div>
      {/* Hero */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', padding: '100px 40px 60px', background: 'linear-gradient(160deg,var(--cream) 0%,var(--soft-orange) 40%,rgba(232,133,26,.18) 100%)' }}>
        <div style={{ position: 'absolute', width: 600, height: 600, top: '-10%', right: '-15%', borderRadius: '50%', border: '1px solid rgba(232,133,26,.08)', animation: 'breathe 6s ease infinite' }} />
        <div style={{ maxWidth: 860, textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <Reveal><div style={{ marginBottom: 20, display: 'inline-block', animation: 'float 4s ease infinite' }}><Logo size={200} /></div></Reveal>
          <Reveal delay={.15}><h1 className="serif" style={{ fontSize: 'clamp(2.8rem,7vw,5.5rem)', fontWeight: 700, color: 'var(--dark)', lineHeight: 1.08, marginBottom: 12 }}>Welcome to <span style={{ color: 'var(--saffron)' }}>Wijjdan</span></h1></Reveal>
          <Reveal delay={.3}><p style={{ fontSize: 'clamp(1rem,2.5vw,1.3rem)', color: 'var(--brown)', fontWeight: 300, letterSpacing: 5, textTransform: 'uppercase', marginBottom: 28 }}>Sufism · Literature · Art · Culture</p></Reveal>
          <Reveal delay={.45}><p className="serif" style={{ fontStyle: 'italic', fontSize: '1.25rem', color: '#7A5E4A', maxWidth: 580, margin: '0 auto 36px', lineHeight: 1.8 }}>"{typed}"<span style={{ display: 'inline-block', width: 2, height: '1em', background: 'var(--saffron)', marginLeft: 4, animation: 'blink 1s infinite', verticalAlign: 'text-bottom' }} /></p></Reveal>
          <Reveal delay={.6}>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <BtnPrimary onClick={() => window.location.href='/poets'}>→ Explore Poets</BtnPrimary>
              <BtnOutline onClick={() => window.location.href='/books'}>Our Books</BtnOutline>
              <BtnOutline href={SOCIAL.youtube + '/live'} target="_blank" style={{ borderColor: '#DC2626', color: '#DC2626' }}>● LIVE — Watch Now</BtnOutline>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Marquee */}
      <div style={{ overflow: 'hidden', background: 'var(--dark)', color: 'var(--gold)', padding: '14px 0', fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 500 }}>
        <div style={{ display: 'flex', whiteSpace: 'nowrap', animation: 'marquee 25s linear infinite' }}><span>{marqueeText}{marqueeText}</span></div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32, padding: '48px 40px', maxWidth: 900, margin: '0 auto' }}>
        <Counter end={stats.articles || 6} suffix="+" label="Published Articles" />
        <Counter end={stats.poets || 8} label="Featured Poets" />
        <Counter end={stats.books || 6} label="Books Published" />
        <Counter end={10} suffix="K+" label="YouTube Subscribers" />
      </div>

      {/* Videos */}
      <section style={{ padding: '80px 40px', maxWidth: 1200, margin: '0 auto' }}>
        <SectionHeader title="From Our" accent="Channel" sub="Watch our latest videos on Sufism, poetry, and cultural events" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 24 }}>
          {videos.map((v, i) => (
            <Reveal key={v.id} delay={i * .1}>
              <Card>
                {playing === v.video_id ? (
                  <div style={{ aspectRatio: '16/9' }}><iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${v.video_id}?autoplay=1`} frameBorder="0" allow="autoplay;encrypted-media" allowFullScreen title={v.title} style={{ display: 'block' }} /></div>
                ) : (
                  <div style={{ position: 'relative', aspectRatio: '16/9', cursor: 'pointer', background: '#1a1a1a', overflow: 'hidden' }} onClick={() => setPlaying(v.video_id)}>
                    <img src={`https://img.youtube.com/vi/${v.video_id}/hqdefault.jpg`} alt={v.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)' }}>
                      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,var(--saffron),var(--saffron-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 22 }}>▶</div>
                    </div>
                  </div>
                )}
                <div style={{ padding: '16px 20px' }}>
                  <h4 className="serif" style={{ fontSize: '1.15rem', marginBottom: 4 }}>{v.title}</h4>
                  <p style={{ fontSize: 13, color: 'var(--brown)', fontWeight: 300 }}>{v.description}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured Articles */}
      {articles.length > 0 && (
        <section style={{ background: 'linear-gradient(180deg,var(--soft-orange),var(--cream))', padding: '80px 40px' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <SectionHeader title="Featured" accent="Articles" sub="Voices from our community" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 24 }}>
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={i * .1}>
                  <Link to={`/articles/${a.id}`}><Card style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Tag type={a.category}>{a.category}</Tag>
                    <h4 className="serif" style={{ fontSize: '1.2rem' }}>{a.title}</h4>
                    <p style={{ fontSize: 13, color: 'var(--brown)', fontWeight: 300, lineHeight: 1.6 }}>{a.excerpt?.substring(0, 100)}...</p>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#999', marginTop: 'auto' }}><span>{a.author_name}</span><span>{a.read_time}</span></div>
                  </Card></Link>
                </Reveal>
              ))}
            </div>
            <Reveal><div style={{ textAlign: 'center', marginTop: 28 }}><BtnPrimary onClick={() => window.location.href='/articles'}>Read All Articles →</BtnPrimary></div></Reveal>
          </div>
        </section>
      )}

      {/* News */}
      <section style={{ padding: '80px 40px', maxWidth: 1200, margin: '0 auto' }}>
        <SectionHeader title="News &" accent="Events" sub="Stay updated with the latest from Wijjdan" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 24 }}>
          {news.map((n, i) => (
            <Reveal key={n.id} delay={i * .08}>
              <Card style={{ padding: 24, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 36, flexShrink: 0 }}>{n.emoji}</div>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                    <Tag type={n.type === 'Event' ? 'event' : undefined}>{n.type}</Tag>
                    <span style={{ fontSize: 12, color: '#999' }}>{n.event_date}</span>
                  </div>
                  <h4 className="serif" style={{ fontSize: '1.15rem', marginBottom: 4 }}>{n.title}</h4>
                  <p style={{ fontSize: 13, color: 'var(--brown)', fontWeight: 300 }}>{n.description}</p>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
        <Reveal><div style={{ textAlign: 'center', marginTop: 28 }}><BtnPrimary onClick={() => window.location.href='/news'}>View All Events →</BtnPrimary></div></Reveal>
      </section>

      {/* Gallery */}
      <section style={{ background: 'var(--dark)', padding: '80px 40px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal><div style={{ textAlign: 'center', marginBottom: 48 }}><h2 className="serif" style={{ fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 700, color: 'white', marginBottom: 8 }}>Image <span style={{ color: 'var(--saffron)' }}>Gallery</span></h2><div style={{ width: 60, height: 3, background: 'linear-gradient(90deg,var(--saffron),var(--gold))', margin: '12px auto 0', borderRadius: 2 }} /></div></Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16 }}>
            {gallery.map((g, i) => (
              <Reveal key={g.id} delay={i * .06}>
                <div style={{ aspectRatio: '1', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', background: g.image_url ? `url(${g.image_url}) center/cover` : g.color, position: 'relative', overflow: 'hidden', transition: 'transform .4s', cursor: 'pointer' }}>
                  {!g.image_url && g.emoji}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(0,0,0,.6),transparent 60%)', display: 'flex', alignItems: 'flex-end', padding: 16, opacity: 0, transition: 'opacity .3s', color: 'white', fontSize: 14, fontWeight: 500 }} className="gal-overlay">{g.title}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal><div style={{ textAlign: 'center', marginTop: 28 }}><BtnOutline onClick={() => window.location.href='/gallery'} style={{ borderColor: 'rgba(255,255,255,.3)', color: 'white' }}>View Full Gallery →</BtnOutline></div></Reveal>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg,var(--saffron),var(--saffron-dark))', padding: '70px 40px', textAlign: 'center', color: 'white' }}>
        <Reveal>
          <h2 className="serif" style={{ fontSize: 'clamp(1.8rem,4vw,2.6rem)', marginBottom: 12 }}>Share Your Voice with the World</h2>
          <p style={{ maxWidth: 500, margin: '0 auto 28px', opacity: .9 }}>Submit your articles on Sufism, poetry, art, and culture.</p>
          <BtnPrimary onClick={() => window.location.href='/submit-article'} style={{ background: 'white', color: 'var(--saffron-dark)' }}>Submit an Article →</BtnPrimary>
        </Reveal>
      </section>

      <style>{`
        .gal-overlay:hover { opacity: 1 !important; }
        @media(max-width:768px) { section { padding: 50px 20px !important; } }
      `}</style>
    </div>
  );
}
